import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['message']
  static values = {
    content: String,
  }

  connect() {
    if (!this.hasContentValue || !this.messageTarget.classList.contains('progressive-text')) {
      return
    }

    this.index = 0
    this.content = this.parseHTML(this.decodeBase64(this.contentValue))
    this.messageTarget.innerHTML = ''
    this.messageTarget.classList.add('typing')
    this.scrollContainer = this.findScrollContainer()
    this.lastRenderTime = null
    this.delay = this.calculateDelay()
    this.render()
  }

  // Decodes a base64 encoded string to a UTF-8 string
  decodeBase64(base64) {
    const binaryString = atob(base64)
    const binaryArray = new Uint8Array(binaryString.length)

    for (let i = 0; i < binaryString.length; i++) {
      binaryArray[i] = binaryString.charCodeAt(i)
    }

    const decoder = new TextDecoder('utf-8')
    return decoder.decode(binaryArray)
  }

  // Finds the nearest scrollable container
  findScrollContainer() {
    let element = this.messageTarget
    while (element && element !== document.body) {
      const style = window.getComputedStyle(element)
      if (['auto', 'scroll'].includes(style.overflowY)) {
        return element
      }
      element = element.parentElement
    }
    return document.documentElement
  }

  // Scrolls the container to the bottom
  scrollToBottom() {
    if (this.scrollContainer) {
      const scrollOptions = {
        top: this.scrollContainer.scrollHeight,
        behavior: 'smooth',
      }

      if (this.scrollContainer === document.documentElement) {
        window.scrollTo(scrollOptions)
      } else {
        this.scrollContainer.scrollTo(scrollOptions)
      }
    }
  }

  // Parses an HTML string into an array of text and nodes
  parseHTML(htmlString) {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlString, 'text/html')
    return Array.from(doc.body.childNodes).flatMap((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent.split(/(\s+)/)
      }
      return node
    })
  }

  // Calculates the delay between rendering blocks of content
  calculateDelay() {
    const length = this.content.length
    if (length < 100) {
      return 40
    } else if (length < 500) {
      return 50
    } else {
      return 80
    }
  }

  // Calculates the size of the content block to render
  calculateBlockSize() {
    const length = this.content.length
    if (length < 150) {
      return Math.floor(Math.random() * 2) + 1
    } else if (length < 750) {
      return Math.floor(Math.random() * 5) + 3
    } else {
      return Math.floor(Math.random() * 20) + 12
    }
  }

  // Renders the content progressively
  render(timestamp) {
    if (!this.lastRenderTime) this.lastRenderTime = timestamp

    const elapsed = timestamp - this.lastRenderTime

    if (elapsed >= this.delay) {
      if (this.index < this.content.length) {
        const blockSize = this.calculateBlockSize()
        const block = this.content.slice(this.index, this.index + blockSize)

        block.forEach((item) => {
          if (typeof item === 'string') {
            this.messageTarget.innerHTML += item
          } else {
            this.messageTarget.appendChild(item.cloneNode(true))
          }
        })

        this.index += blockSize
        this.scrollToBottom()

        this.lastRenderTime = timestamp
      } else {
        this.messageTarget.classList.remove('typing')
        this.scrollToBottom()
        return
      }
    }

    requestAnimationFrame(this.render.bind(this))
  }
}
